/** Agent execution API client: active runs and single-run snapshots. */

import { apiRequest } from "@/api/client";

export interface ActiveExecutionResponse {
  native_subagent?: boolean;
  id: string;
  status: string;
  project_id?: string;
  agent_name: string | null;
  agent_key: string | null;
  task_id: string | null;
  task_title: string | null;
  started_at: string;
  /** When a paused run paused; null for every other status. */
  paused_at?: string | null;
  conversation_id: string;
  runtime_session_id: string | null;
  origin: "chat" | "heartbeat";
}

/** One run as the execution detail endpoint returns it. */
export interface ExecutionSnapshot {
  id: string;
  status: string;
  error_message?: string;
  conversation_id?: string | null;
}

/**
 * Fetch every active execution for the current user, across all projects.
 * Used by the browser to reconcile its in-memory execution map on reconnect.
 */
export async function fetchAllActiveExecutions(
  signal?: AbortSignal,
): Promise<ActiveExecutionResponse[]> {
  type WireExecution =
    | ActiveExecutionResponse
    | (Omit<ActiveExecutionResponse, "id"> & { execution_id: string });
  const res = await apiRequest<{ data: WireExecution[] }>(
    `/api/v1/executions/active/`,
    { silent: true, signal },
  );
  return res.data.map((execution) => ({
    ...execution,
    id: "execution_id" in execution ? execution.execution_id : execution.id,
  }));
}

/** Read a missed terminal transition without guessing success from absence. */
export async function getExecutionSnapshot(
  projectId: string,
  executionId: string,
  signal?: AbortSignal,
): Promise<ExecutionSnapshot> {
  const response = await apiRequest<{ data: ExecutionSnapshot }>(
    `/api/v1/projects/${projectId}/executions/${executionId}/`,
    { silent: true, signal },
  );
  return response.data;
}
